import { useOrgContext } from '@/org/contexts/OrgContext'
import { CircleFragment, RoleSummaryFragment } from '@gql'
import { useMemo } from 'react'
import {
  getCirclePermissionsOf,
  useCirclePermissionsInputs,
} from './useCirclePermissions'

export interface CircleParentLink {
  circle: CircleFragment
  role: RoleSummaryFragment
  // A single-member link role that is taken has no room left
  canTakeMember: boolean
  // Rights on the link role itself, not on the circle it leads
  canEditMembers: boolean
}

// The parent-link roles of a circle: the roles through which someone leads or
// represents it, and therefore the target when a member is added to the circle
// from the outside.
//
// `parentLink` is the ONLY criterion. Never match on the name "Leader" and
// never require `base`: an org may run a home-made link role, named anything
// and tied to no base role, and it takes the member exactly like a seeded
// Leader would.
export default function useCircleParentLinks(
  circleId?: string
): CircleParentLink[] {
  const { orgData } = useOrgContext()
  const permissionsInputs = useCirclePermissionsInputs()

  return useMemo(() => {
    if (!orgData || !circleId) return []
    return orgData
      .childrenOf(circleId)
      .map((circle) => {
        const role = orgData.getRole(circle.roleId)
        if (!role?.parentLink) return undefined
        return {
          circle,
          role,
          canTakeMember:
            !role.singleMember || orgData.membersOf(circle.id).length === 0,
          canEditMembers: getCirclePermissionsOf(circle.id, permissionsInputs)
            .canEditMembers,
        }
      })
      .filter((link): link is CircleParentLink => !!link)
  }, [orgData, circleId, permissionsInputs])
}
